import { useEffect, useState } from 'react';

import { ArrowRightIcon, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { TrackingEvents } from '../../analytics/events';
import { trackCustomEvent } from '../../analytics/Mixpanel';
import { cn } from '@/helpers/shadCnUtils';

const BANNER_DISMISSED_KEY = 'wexler-2-0-banner-dismissed';
const BANNER_SESSION_DISMISSED_KEY = 'wexler-2-0-banner-session-dismissed';
const BANNER_DISMISS_COUNT_KEY = 'wexler-2-0-banner-dismiss-count';

const marqueeItems = [
  ' ',
  'Wexler 2.0 is coming soon!',
  '|',
  'Analyse all documents in your Matter',
  '|',
  'Updated UI',
  '|',
  'Automated document batching',
];

export const AnnouncementBanner = () => {
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY) === 'true';

    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    const sessionDismissed = sessionStorage.getItem(BANNER_SESSION_DISMISSED_KEY) === 'true';
    setIsDismissed(sessionDismissed);
  }, []);

  const handleDismiss = () => {
    trackCustomEvent(TrackingEvents.ClickAnnouncementBannerDismiss);
    const dismissCount = Number(localStorage.getItem(BANNER_DISMISS_COUNT_KEY) ?? '0') + 1;
    localStorage.setItem(BANNER_DISMISS_COUNT_KEY, dismissCount.toString());

    if (dismissCount >= 2) {
      localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
      sessionStorage.removeItem(BANNER_SESSION_DISMISSED_KEY);
    } else {
      sessionStorage.setItem(BANNER_SESSION_DISMISSED_KEY, 'true');
    }

    setIsDismissed(true);
  };

  if (isDismissed) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative isolate overflow-hidden bg-gradient-to-r from-teal-600 via-emerald-500 to-teal-500 text-white shadow-lg',
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.6), transparent 45%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.35), transparent 35%)',
        }}
      />
      <div className="flex flex-wrap items-center gap-3 px-4 py-1 sm:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="relative flex-1 overflow-hidden">
            <div className="animate-marquee flex w-max whitespace-nowrap">
              {[0, 1].map((loopIndex) => (
                <div
                  key={`marquee-loop-${loopIndex}`}
                  className="flex flex-none items-center gap-8 text-sm font-semibold tracking-tight"
                  aria-hidden={loopIndex === 1}
                >
                  {marqueeItems.map((item, index) => (
                    <span key={`announcement-${loopIndex}-${index}`} className="flex items-center gap-2">
                      {item}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        <Link
          to="/app/announcement"
          onClick={() => trackCustomEvent(TrackingEvents.ClickAnnouncementBannerLink)}
          className="group inline-flex items-center gap-1 rounded-full border border-white/30 bg-white/10 px-3 text-sm font-semibold tracking-wide text-white transition hover:bg-white/20"
        >
          Learn more
          <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </Link>
        <button
          onClick={handleDismiss}
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/10 transition hover:bg-white/20"
          aria-label="Dismiss banner"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};
